import { cn } from '@nwwp/ui-shadcn/lib/utils'

function toXY(deg: number, r: number, cx: number, cy: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

const R = 78
const CX = 100
const CY = 100
const ARC_LENGTH = R * Math.PI * 1.5

type ArcGaugeProps = {
  /** 0–1 fill of the arc */
  fill: number
  color: string
  label: string
  display: string
  'aria-label'?: string
  className?: string
}

/** Semicircle progress gauge used by self-serve calculators. */
function ArcGauge({ fill, color, label, display, 'aria-label': ariaLabel, className }: ArcGaugeProps) {
  const pct = Math.min(Math.max(fill, 0), 1)
  const start = toXY(-135, R, CX, CY)
  const end = toXY(135, R, CX, CY)
  const arcPath = `M ${start.x} ${start.y} A ${R} ${R} 0 1 1 ${end.x} ${end.y}`

  return (
    <svg
      viewBox="0 0 200 145"
      className={cn('mx-auto block w-full max-w-60', className)}
      aria-label={ariaLabel ?? `${label}: ${display}`}
      data-slot="arc-gauge"
    >
      <path
        d={arcPath}
        fill="none"
        stroke="var(--color-charcoal)"
        strokeWidth="10"
        strokeLinecap="round"
        opacity="0.3"
      />
      <path
        d={arcPath}
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={ARC_LENGTH}
        strokeDashoffset={ARC_LENGTH * (1 - pct)}
        style={{ transition: 'stroke-dashoffset .7s cubic-bezier(.4,0,.2,1), stroke .3s ease' }}
      />
      <text
        x={CX}
        y={CY - 4}
        textAnchor="middle"
        style={{ fontSize: '26px', fontWeight: 700, fill: 'var(--color-white)', fontFamily: 'inherit' }}
      >
        {display}
      </text>
      <text
        x={CX}
        y={CY + 18}
        textAnchor="middle"
        style={{
          fontSize: '12px',
          fontWeight: 500,
          fill: color,
          fontFamily: 'inherit',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </text>
    </svg>
  )
}

export { ArcGauge }
export type { ArcGaugeProps }
